import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  TranslationsService,
  parseTranslationToTreeItem,
} from '../translations.service';
import { sortTreeRecursively } from '../translations.tree';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  createTranslation,
  createTranslationFailure,
  createTranslationSuccess,
  deleteTranslation,
  deleteTranslationFailure,
  deleteTranslationSuccess,
  loadTranslations,
  loadTranslationsFailure,
  loadTranslationsSuccess,
  updateTranslation,
  updateTranslationFailure,
  updateTranslationSuccess,
} from './translations.actions';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { retryTransient } from '../../../common/http/retry-transient';
import { flattenTranslations } from '../../../common/flatten-translations';
import {
  selectTranslations,
  selectTranslationsLoaded,
} from './translations.selectors';
import {
  ITranslations,
  ITranslationsRequest,
  IDsTreeItem,
} from '../translations.interfaces';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DsToastService } from '@bmw-ds/components';

const SAVE_FAILED_KEY = 'administration.translations.errors.saveFailed';
const SUCCESS_TOAST_KEYS: Record<string, string> = {
  [createTranslationSuccess.type]:
    'administration.translations.toasts.createSuccess',
  [updateTranslationSuccess.type]:
    'administration.translations.toasts.updateSuccess',
  [deleteTranslationSuccess.type]:
    'administration.translations.toasts.deleteSuccess',
};

@Injectable()
export class TranslationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly translationsService = inject(TranslationsService);
  private readonly toastService = inject(DsToastService);

  public readonly loadTranslations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTranslations),
      withLatestFrom(this.store.select(selectTranslationsLoaded)),
      filter(([, alreadyLoaded]) => !alreadyLoaded),
      exhaustMap(() =>
        this.translationsService.getTranslations().pipe(
          retryTransient(),
          map((translations: ITranslations) => {
            const dsTreeItems: IDsTreeItem[] =
              parseTranslationToTreeItem(translations);
            sortTreeRecursively(dsTreeItems);
            return loadTranslationsSuccess({
              translations: { ...translations },
              loading: false,
              dsTreeItems,
            });
          }),
          catchError((error) => {
            console.error('Failed to load translations', error);
            return of(loadTranslationsFailure({ error, loading: false }));
          })
        )
      )
    )
  );

  public readonly createTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createTranslation),
      mergeMap(({ translation }) =>
        this.translationsService.createTranslation(translation).pipe(
          map((createdTranslation: ITranslationsRequest) =>
            createTranslationSuccess({
              translation: createdTranslation,
              loading: false,
            })
          ),
          catchError((error) =>
            of(createTranslationFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public readonly updateTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTranslation),
      mergeMap(({ translation }) =>
        this.translationsService.updateTranslation(translation).pipe(
          map((updatedTranslation: ITranslationsRequest) =>
            updateTranslationSuccess({
              translation: updatedTranslation,
              loading: false,
            })
          ),
          catchError((error) =>
            of(updateTranslationFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public readonly deleteTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTranslation),
      mergeMap(({ translation }) =>
        this.translationsService.deleteTranslation(translation).pipe(
          map(() => deleteTranslationSuccess({ translation, loading: false })),
          catchError((error) =>
            of(deleteTranslationFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public readonly reloadOnWriteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          createTranslationSuccess,
          updateTranslationSuccess,
          deleteTranslationSuccess
        ),
        withLatestFrom(this.store.select(selectTranslations)),
        tap(([{ translation }, translations]) =>
          this.translateService.setTranslation(
            translation.locale,
            flattenTranslations('', translations[translation.locale] ?? {}),
            false
          )
        )
      ),
    { dispatch: false }
  );

  public readonly notifyWriteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          createTranslationSuccess,
          updateTranslationSuccess,
          deleteTranslationSuccess
        ),
        tap((action) =>
          this.toastService.pushToast({
            id: 'translation-saved',
            tone: 'positive',
            toastText: this.translateService.instant(
              SUCCESS_TOAST_KEYS[action.type]
            ),
          })
        )
      ),
    { dispatch: false }
  );

  public readonly notifyWriteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          createTranslationFailure,
          updateTranslationFailure,
          deleteTranslationFailure
        ),
        tap(({ error }) =>
          this.toastService.pushToast({
            id: 'translation-save-failed',
            tone: 'critical',
            toastText: this.failureToastText(error),
          })
        )
      ),
    { dispatch: false }
  );

  private failureToastText(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const message = error.error?.message;
      if (typeof message === 'string') {
        return message;
      }
      return error.message;
    }
    return this.translateService.instant(SAVE_FAILED_KEY);
  }
}
